import i18next from "i18next";
import { FoaPart, ProcedureType, type Regulation } from "../../autogen/bff-api";
import { isAtOrAboveEea, tenderDeadlineProvision } from "../procurement/karens";
import { runsBelowEeaThreshold } from "../procurement/procedure";
import { offeredProcedures, procedureAvailability, type ProcedureInputs } from "../procurement/procedureAvailability";
import {
  procedureProvisionKey,
  procedureShownAs,
  procedureTypeOptionsFor,
} from "../procurement/publicProcurementOptions";
import { procedureInputsAfter } from "./confirmationCardEditing";
import { currentValueAt } from "./confirmationCardFormatting";
import { namePattern, withEachSentence } from "./statedBeforeValues";
import { MODEL_LANGUAGE } from "./tools";

type Row = Record<string, unknown>;

type ProcedureWording = { cardNames: string[]; otherPartNames: string[]; otherPartProvisions: Set<string> };

const PROCEDURE = ["publicContext", "procedureType"];
const PARTS: readonly FoaPart[] = [FoaPart.PartI, FoaPart.PartIi, FoaPart.PartIii, FoaPart.PartIv];
const PROVISION = /(?<![\p{L}])(FOA|anskaffelsesforskriften|forsyningsforskriften)\s+§§?\s*(\d+(?:-\d+)?)/giu;

export const namesProcedureOfOtherPart = (text: string | null, patch: Row, current: Row | undefined): boolean => {
  if (!text) return false;
  const wording = procedureWording(patch, current);
  return wording !== null && namesOtherPart(text, wording);
};

export const withoutProcedureOfOtherPart = (text: string, patch: Row, current: Row | undefined): string => {
  const wording = procedureWording(patch, current);
  if (!wording) return text;
  return withEachSentence(text, (sentence) => (namesOtherPart(sentence, wording) ? null : sentence));
};

export const withoutOtherStoredPart = (text: string, stored: Row): string => {
  const regulation = currentValueAt(stored, ["eventContext", "regulation"]) as Regulation | undefined;
  const foaPart = currentValueAt(stored, ["eventContext", "foaPart"]);
  if (!regulation || !PARTS.includes(foaPart as FoaPart)) return text;
  const aboveEea = isAtOrAboveEea(regulation, foaPart as FoaPart);
  return withEachSentence(text, (sentence) => {
    const namesOtherPartName = [...sentence.matchAll(PART_NAMED)].some(([, numeral]) => PART_NUMERALS[numeral] !== foaPart);
    const misplacesEea = aboveEea ? BELOW_EEA.test(sentence) : ABOVE_EEA.test(sentence);
    return namesOtherPartName || misplacesEea ? null : sentence;
  });
};

const PART_NAMED = /(?<![\p{L}])[Dd]el\s+(IV|III|II|I)(?![\p{L}])/gu;
const PART_NUMERALS: Record<string, FoaPart> = {
  I: FoaPart.PartI,
  II: FoaPart.PartIi,
  III: FoaPart.PartIii,
  IV: FoaPart.PartIv,
};
const ABOVE_EEA = /(?<![\p{L}])over\s+EØS-terskel/iu;
const BELOW_EEA = /(?<![\p{L}])under\s+EØS-terskel/iu;

const namesOtherPart = (text: string, wording: ProcedureWording): boolean => {
  const withoutCardNames = wording.cardNames.reduce((rest, name) => rest.replace(namePattern(name, "giu"), " "), text);
  return (
    wording.otherPartNames.some((name) => namePattern(name).test(withoutCardNames)) ||
    provisionsIn(text).some((provision) => wording.otherPartProvisions.has(provision))
  );
};

const procedureWording = (patch: Row, current: Row | undefined): ProcedureWording | null => {
  const proposed = isRow(patch.publicContext) ? patch.publicContext.procedureType : undefined;
  if (!current || typeof proposed !== "string") return null;
  const inputs = procedureInputsAfter(current, patch);
  if (!inputs.regulation || !inputs.foaPart || !PARTS.includes(inputs.foaPart)) return null;
  const stored = currentValueAt(current, PROCEDURE);
  const cardTypes = [proposed, stored].filter((type): type is ProcedureType => typeof type === "string");
  const misnamed = misnamedIn(inputs);
  const cardNames = unique(
    cardTypes.flatMap((type) => [type, procedureShownAs(type, inputs.regulation, inputs.foaPart) ?? type]).map(nameOf),
  );
  const here = new Set(
    provisionsOf(inputs, [...offeredProcedures(inputs), ...cardTypes.filter((type) => !misnamed.includes(type))]),
  );
  const elsewhere = PARTS.filter((part) => part !== inputs.foaPart).flatMap((foaPart) => {
    const there = { ...inputs, foaPart };
    return provisionsOf(there, offeredProcedures(there));
  });
  return {
    cardNames,
    otherPartNames: unique(misnamed.map(nameOf)).filter((name) => !cardNames.includes(name)),
    otherPartProvisions: new Set(elsewhere.filter((provision) => !here.has(provision))),
  };
};

const misnamedIn = (inputs: ProcedureInputs): ProcedureType[] => {
  const { regulation, foaPart } = inputs;
  const partIHealthSocialEducation = procedureAvailability(inputs).partIHealthSocialEducation;
  const named = procedureTypeOptionsFor(regulation, foaPart, partIHealthSocialEducation).map((option) => option.value);
  return Object.values(ProcedureType).filter((type) =>
    runsBelowEeaThreshold(type) ? !named.includes(type) : procedureShownAs(type, regulation, foaPart) !== type,
  );
};

const provisionsOf = (inputs: ProcedureInputs, types: readonly ProcedureType[]): string[] => {
  const partIHealthSocialEducation = procedureAvailability(inputs).partIHealthSocialEducation;
  return types
    .flatMap((type) => [
      procedureProvisionKey(inputs.regulation, type, inputs.foaPart, partIHealthSocialEducation),
      tenderDeadlineProvision(inputs.regulation, inputs.foaPart, type),
    ])
    .flatMap((text) => (text ? provisionsIn(text) : []));
};

const provisionsIn = (text: string): string[] =>
  [...text.matchAll(PROVISION)].map(
    (match) => `${match[1].toLowerCase() === "forsyningsforskriften" ? "forsyningsforskriften" : "FOA"} § ${match[2]}`,
  );

const nameOf = (type: string): string => String(i18next.t(type, { lng: MODEL_LANGUAGE }));

const unique = (values: string[]): string[] => [...new Set(values)];

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
